/**
 * Who is signed in.
 *
 * One context rather than a query, because this is not server state in the
 * React Query sense: it changes only when somebody signs in or out, every
 * screen reads it, and a stale-while-revalidate refetch of "who am I" would be
 * a strange thing to show a loading spinner for.
 *
 * The one asynchronous moment is at start-up, when a stored token has to be
 * checked against the server before the app can know whether to show the portal
 * or the sign-in page. Until that resolves the state is `loading` — and the
 * distinction matters: rendering the sign-in page while the check is in flight
 * would flash a login form at somebody who is already signed in.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authApi from '../services/api/auth';
import type { SignedInUser, UserRole } from '../services/api/auth';
import { setUnauthenticatedHandler } from '../services/api/client';

interface AuthState {
  user: SignedInUser | null;
  /** True until the stored token has been checked. Never true again after. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Change the signed-in password, and fold the new expiry into the user.
   *
   * Here rather than called straight from the form, because the warning banner
   * and the forced-change guard both read `user.password`. A component that
   * changed the password without telling the context would leave somebody
   * staring at a red alert about a password they have just replaced.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Convenience for the many places that ask "may they see staff screens?". */
  isStaff: boolean;
  hasRole: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SignedInUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Resolve the stored token once, at start-up.
  useEffect(() => {
    let cancelled = false;
    void authApi
      .fetchCurrentUser()
      .then((found) => {
        if (!cancelled) setUser(found);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The API telling us the session is gone — expired, revoked, or the account
   * disabled. Clearing the user here is what turns a wall of failing panels
   * into the sign-in page.
   */
  useEffect(() => {
    setUnauthenticatedHandler(() => setUser(null));
    return () => setUnauthenticatedHandler(null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setUser(await authApi.login(email, password));
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const password = await authApi.changePassword(currentPassword, newPassword);
    // Merged rather than refetched: the API has just told us the new state,
    // and a round trip to /auth/me would be a second chance to fail after the
    // password has already changed.
    setUser((current) => (current ? { ...current, password } : current));
  }, []);

  const signOut = useCallback(async () => {
    // Cleared in a finally, not after the await.
    //
    // `authApi.logout` swallows its own network failure today, but if that ever
    // stops being true the person clicking Sign out must still end up signed
    // out of the machine in front of them. Leaving them in the portal because a
    // revoke request failed is the one outcome nobody expects from this button.
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      /*
       * Everything React Query is holding goes too.
       *
       * Not housekeeping. The cache holds whatever the last person looked at —
       * the volunteer directory, a profile, the dashboard figures — keyed by
       * query and not by account. Without this, the next person to sign in on
       * the same machine is served the previous session’s data from memory
       * while their own request is still in flight, and on a shared office
       * machine that is somebody else’s contact details on screen.
       *
       * `clear`, not `invalidateQueries`: invalidation marks data stale and
       * keeps showing it until the refetch lands, which is the exact window
       * this is closing.
       */
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn,
      signOut,
      changePassword,
      isStaff: user?.role === 'ADMIN' || user?.role === 'TEAM',
      hasRole: (...roles: UserRole[]) => (user ? roles.includes(user.role) : false),
    }),
    [user, loading, signIn, signOut, changePassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * `const { user, signOut } = useAuth()`.
 *
 * Throws outside the provider rather than returning a null user: a silent null
 * would render the sign-in page inside the portal shell and look like a bug in
 * the session rather than a missing provider.
 */
export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) throw new Error('useAuth must be used inside <AuthProvider>.');
  return state;
}
